import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ts, P } from '../api';
import { Page, Section, Field, Select, Table, Td, Pill, Empty, Loading, ErrorNote, useLoad, useTS, when } from '../ui';

export const STAGE_LABELS = {
  new: 'New', contact_attempted: 'Contact attempted', connected: 'Connected', qualified: 'Qualified',
  appointment_scheduled: 'Appointment scheduled', proposal_sent: 'Proposal sent', won: 'Won', lost: 'Lost',
  follow_up_required: 'Follow-up required',
};
const STAGES = Object.keys(STAGE_LABELS);

export default function TSLeads() {
  const session = useTS();
  const [filters, setFilters] = useState({ stage: '', q: '' });
  const leads = useLoad(() => ts.leads(filters), [filters.stage, filters.q]);
  const staff = useLoad(() => (session.can(P.LEADS_ASSIGN) ? ts.staff() : Promise.resolve([])));
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState('');

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await ts.importLeads(file);
      toast.success(`Imported ${r.created}. Skipped ${r.duplicates_skipped} duplicates and ${r.invalid} invalid rows.`);
      leads.reload();
    } catch (err) {
      toast.error(err.message);
    }
    e.target.value = '';
  };

  return (
    <Page title="Leads" subtitle={session.can(P.LEADS_ALL) ? 'Every lead in the company' : 'Leads assigned to you'}
      actions={(
        <>
          {session.can(P.LEADS_ASSIGN) && (
            <label className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border text-sm cursor-pointer">
              <Upload className="w-4 h-4" /> Import CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} />
            </label>
          )}
          <Button size="sm" onClick={() => setAdding(true)} data-testid="ts-add-lead"><Plus className="w-4 h-4 mr-1" /> Add lead</Button>
        </>
      )}>
      {adding && <AddLead staff={staff.data || []} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); leads.reload(); }} />}
      <Section>
        <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); setFilters({ ...filters, q }); }}>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone, email" className="w-64 h-9" />
          <Select value={filters.stage} onChange={(stage) => setFilters({ ...filters, stage })} options={[['', 'All stages'], ...STAGES.map(s => [s, STAGE_LABELS[s]])]} />
          <Button size="sm" variant="outline" type="submit">Search</Button>
        </form>
        {leads.loading ? <Loading /> : (leads.data || []).length === 0 ? <Empty>No leads yet.</Empty> : (
          <Table head={['Name', 'Phone', 'Stage', 'Owner', 'Updated']}>
            {leads.data.map(l => (
              <tr key={l.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setOpen(l.id)} data-testid={`ts-lead-${l.id}`}>
                <Td className="font-medium">{l.name}{(l.tags || []).map(t => <Pill key={t} tone="gray">{t}</Pill>)}</Td>
                <Td>{l.phone || l.email}</Td>
                <Td><Pill tone="blue">{STAGE_LABELS[l.ts_stage] || l.ts_stage}</Pill></Td>
                <Td>{(staff.data || []).find(m => m.id === l.assigned_member_id)?.full_name || (l.assigned_member_id ? '—' : 'Unassigned')}</Td>
                <Td>{when(l.updated_at)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
      {open && <LeadDetail id={open} staff={staff.data || []} onClose={() => setOpen(null)} onChanged={leads.reload} />}
    </Page>
  );
}

function AddLead({ staff, onClose, onSaved }) {
  const session = useTS();
  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '', notes: '', source: 'manual', assigned_member_id: '' });
  const [error, setError] = useState('');
  const set = (k) => (v) => setForm({ ...form, [k]: typeof v === 'string' ? v : v.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await ts.createLead({ ...form, assigned_member_id: form.assigned_member_id || undefined });
      if (r.duplicate_of) toast.message(`${r.name} is already a lead — opened the existing one.`);
      else toast.success('Lead added');
      onSaved();
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <Section title="New lead">
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ErrorNote error={error} />
        <Field label="Name"><Input value={form.name} onChange={set('name')} data-testid="ts-lead-name" /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={set('phone')} data-testid="ts-lead-phone" /></Field>
        <Field label="Email"><Input type="email" value={form.email} onChange={set('email')} /></Field>
        <Field label="Company"><Input value={form.company} onChange={set('company')} /></Field>
        {session.can(P.LEADS_ASSIGN) && (
          <Field label="Assign to"><Select value={form.assigned_member_id} onChange={set('assigned_member_id')} options={[['', 'Unassigned'], ...staff.filter(m => m.status === 'active').map(m => [m.id, m.full_name])]} /></Field>
        )}
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" data-testid="ts-lead-save">Save lead</Button>
        </div>
      </form>
    </Section>
  );
}

export function LeadDetail({ id, staff, onClose, onChanged }) {
  const session = useTS();
  const { data, loading, reload } = useLoad(() => ts.lead(id), [id]);
  const [note, setNote] = useState('');
  const change = async (fn) => {
    try { await fn(); reload(); onChanged?.(); } catch (err) { toast.error(err.message); }
  };
  if (loading && !data) return <Section><Loading /></Section>;
  if (!data) return null;
  const l = data.lead;
  return (
    <Section title={l.name} actions={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}>
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <span>{l.phone}</span>{l.email && <span className="text-muted-foreground">{l.email}</span>}
        {l.phone && session.can(P.CALL) && <Link className="text-primary text-xs" to={`/telesales/phone?to=${encodeURIComponent(l.phone)}&lead=${l.id}`}>Call</Link>}
        {l.phone && session.can(P.SMS) && <Link className="text-primary text-xs" to={`/telesales/phone?tab=sms&to=${encodeURIComponent(l.phone)}`}>Text</Link>}
      </div>
      <div className="flex flex-wrap gap-3">
        <Field label="Stage">
          <Select value={l.ts_stage} onChange={(s) => change(() => ts.updateLead(l.id, { ts_stage: s }))} options={STAGES.map(s => [s, STAGE_LABELS[s]])} data-testid="ts-lead-stage" />
        </Field>
        {session.can(P.LEADS_ASSIGN) && (
          <Field label="Owner">
            <Select value={l.assigned_member_id || ''} onChange={(m) => change(() => ts.assignLead(l.id, m || null))}
              options={[['', 'Unassigned'], ...staff.filter(m => m.status === 'active').map(m => [m.id, m.full_name])]} />
          </Field>
        )}
        <Field label="Follow up on">
          <Input type="datetime-local" className="h-9" value={(l.follow_up_at || '').slice(0, 16)}
            onChange={(e) => change(() => ts.updateLead(l.id, { follow_up_at: e.target.value ? new Date(e.target.value).toISOString() : null }))} />
        </Field>
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (note.trim()) change(async () => { await ts.addNote(l.id, note); setNote(''); }); }}>
        <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note" className="h-9" />
        <Button size="sm" type="submit">Add</Button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div>
          <div className="font-medium mb-1">Notes</div>
          <ul className="space-y-1 text-muted-foreground" data-testid="ts-lead-notes">
            {l.notes && <li>{l.notes}</li>}
            {(l.notes_history || []).slice().reverse().map((h, i) => <li key={i}>{when(h.created_at)} — {h.text}{h.by ? ` (${h.by})` : ''}</li>)}
          </ul>
        </div>
        <div>
          <div className="font-medium mb-1">Calls</div>
          <ul className="space-y-1 text-muted-foreground">{data.calls.map(c => <li key={c.id}>{when(c.created_at)} {c.direction} · {c.status} · {c.member_name || 'AI'}{c.summary ? ` — ${c.summary}` : ''}</li>)}</ul>
        </div>
        <div>
          <div className="font-medium mb-1">Texts & callbacks</div>
          <ul className="space-y-1 text-muted-foreground">
            {data.messages.map(m => <li key={m.id}>{when(m.created_at)} {m.direction === 'outbound' ? '→' : '←'} {m.body}</li>)}
            {data.callbacks.map(c => <li key={c.id}>Callback {c.status} · due {when(c.due_at)}</li>)}
          </ul>
        </div>
      </div>
    </Section>
  );
}
